import * as Sentry from "@sentry/react";

/**
 * Initialize Sentry for error tracking and performance monitoring
 * @param {Object} options - Sentry initialization options
 * @param {string} options.dsn - Sentry DSN (Data Source Name)
 * @param {string} options.environment - Environment (development, staging, production)
 * @param {Object} options.user - Optional user information
 */
export const initSentry = (options = {}) => {
  const { dsn, environment = import.meta.env.MODE, user, ...restOptions } = options;

  if (!dsn) {
    console.warn("⚠️ Sentry DSN is not provided. Error tracking will be disabled.");
    return;
  }

  Sentry.init({
    dsn,
    environment,
    integrations: [
      Sentry.browserTracingIntegration(),
      Sentry.replayIntegration({
        maskAllText: true,
        blockAllMedia: true,
      }),
    ],
    // Performance Monitoring
    tracesSampleRate: environment === "production" ? 0.1 : 1.0, // 10% in production, 100% in dev
    // Session Replay
    replaysSessionSampleRate: environment === "production" ? 0.1 : 1.0, // 10% in production
    replaysOnErrorSampleRate: 1.0, // 100% of sessions with an error
    // Release tracking
    release: import.meta.env.VITE_SENTRY_RELEASE || undefined,
    // Additional options
    ...restOptions,
  });

  // Set user context if provided
  if (user) {
    Sentry.setUser(user);
  }

  console.log("✅ Sentry initialized successfully");
};

/**
 * Set user context for Sentry
 * @param {Object} user - User information
 */
export const setSentryUser = (user) => {
  Sentry.setUser(user);
};

/**
 * Clear user context (e.g., on logout)
 */
export const clearSentryUser = () => {
  Sentry.setUser(null);
};

/**
 * Capture an exception manually
 * @param {Error} error - Error object
 * @param {Object} context - Additional context
 */
export const captureException = (error, context = {}) => {
  Sentry.captureException(error, {
    extra: context,
  });
};

/**
 * Capture a message
 * @param {string} message - Message to capture
 * @param {string} level - Log level (info, warning, error)
 */
export const captureMessage = (message, level = "info") => {
  Sentry.captureMessage(message, level);
};

/**
 * Add breadcrumb for debugging
 * @param {Object} breadcrumb - Breadcrumb data
 */
export const addBreadcrumb = (breadcrumb) => {
  Sentry.addBreadcrumb(breadcrumb);
};

/**
 * Set additional context/tags
 * @param {Object} context - Context object
 */
export const setContext = (name, context) => {
  Sentry.setContext(name, context);
};

/**
 * Set tag for filtering in Sentry
 * @param {string} key - Tag key
 * @param {string} value - Tag value
 */
export const setTag = (key, value) => {
  Sentry.setTag(key, value);
};

